import React, { useRef, useEffect, useCallback, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { useStore } from '../store'
import { useScrollPosition } from '../../hooks/useScrollPosition'
import styles from './scrollViewPct.m.scss'

/**
 * A component to get the percentage of the div in the viewport
 *
 * @param {React.ReactNode} children The content of the parent div.
 * @param {() => void} onScroll Event fired when the scroll is changed.
 * @param {boolean} disappear Percentage from top to bottom or bottom to top.
 */

export const ScrollViewPct = ({
  children,
  onScroll,
  disappear,
  fromTop,
  padding,
  debug
}: {
  children: React.ReactNode
  onScroll: (pct: number) => void
  disappear: boolean
  fromTop: boolean
  padding: {
    top: number
    bottom: number
  }
  debug: boolean
}) => {
  const pct = useStore()
  const ref = useRef<HTMLDivElement>(null)
  const scrollPosition = useScrollPosition()
  const [offsetTop, setOffsetTop] = useState(
    (window.innerHeight * padding.top) / 100
  )
  const [offsetBottom, setOffsetBottom] = useState(
    (window.innerHeight * padding.bottom) / 100
  )

  const handleResize = useCallback(() => {
    setOffsetTop((window.innerHeight * padding.top) / 100)
    setOffsetBottom((window.innerHeight * padding.bottom) / 100)
  }, [])

  // Optimize by using requestAnimationFrame.
  // Do not trigger the callback on scroll but on the next frame.
  useEffect(() => {
    if (ref.current) {
      const height =
        ref.current.getBoundingClientRect().height + offsetTop + offsetBottom
      const top = ref.current.getBoundingClientRect().top - offsetTop
      const diff = Math.abs(height - window.innerHeight)
      const bottom = top + height - window.innerHeight

      if (debug) {
        console.log(
          top,
          top - window.innerHeight,
          Math.max(Math.min(top - window.innerHeight, 0))
        )
      }

      if (disappear) {
        if (bottom < 0 && bottom > -height - diff) {
          useStore.setState({
            pct: Math.min(Math.max(-bottom, 0)) / (height + diff)
          })
          // onScroll(Math.min(Math.max(-bottom, 0)) / (height + diff))
        }
      } else {
        if (fromTop) {
          if (
            top - window.innerHeight < 0 &&
            top - window.innerHeight > -height
          ) {
            useStore.setState({
              pct: -Math.max(Math.min(top - window.innerHeight, 0)) / height
            })
            // onScroll(-Math.max(Math.min(top - window.innerHeight, 0)) / height)
          }
        } else {
          if (top < 0 && top > -height) {
            useStore.setState({ pct: Math.min(Math.max(-top, 0)) / height })
            // onScroll(Math.min(Math.max(-top, 0)) / height)
          }
        }
      }
    }
  }, [scrollPosition])

  useEffect(() => {
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <div className={styles.container} ref={ref}>
      {children}
    </div>
  )
}

ScrollViewPct.defaultProps = {
  disappear: false,
  padding: {
    top: 0,
    bottom: 0
  },
  debug: false,
  fromTop: false
}
